/**
 * @architecture-header 1
 * @layer frontend-package
 * @domain api-client
 * @responsibility Normalize transport failures into the shared Vispo API error contract.
 * @state-owner none
 * @allowed-imports @vispo/contracts
 * @forbidden host environment; UI; query cache
 * @public-api @vispo/api-client
 * @side-effects none
 * @tests create-client.test.ts
 */

import { type ApiError, apiErrorSchema } from '@vispo/contracts';

export class VispoApiError extends Error {
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string;
  readonly status: number;

  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = 'VispoApiError';
    this.code = error.code;
    this.details = error.details;
    this.requestId = error.requestId;
    this.status = status;
  }
}

export function normalizeApiError(value: unknown, status: number): VispoApiError {
  const parsed = apiErrorSchema.safeParse(value);
  if (parsed.success) return new VispoApiError(parsed.data, status);
  if (value && typeof value === 'object' && 'error' in value) {
    const nested = apiErrorSchema.safeParse(value.error);
    if (nested.success) return new VispoApiError(nested.data, status);
  }

  return new VispoApiError(
    {
      code: 'transport_error',
      message: `Request failed with status ${status}`,
      details: value,
      requestId: 'unavailable',
    },
    status,
  );
}
